import { ReactNode, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { accessTokenKey, refreshTokenKey } from '../../../helpers/local-storage';
import { concatPath } from '../../../helpers/path';
import { ServiceType } from '../../../helpers/services';
import { useIsMobile } from '../../../hooks/use-is-mobile';
import { useLang } from '../../../hooks/use-lang';
import { useQueryParams } from '../../../hooks/use-query-params';
import { AuthPath, ManagerPath, RootPath } from '../../../routes/manager/manager-enums';
import { UserRole } from '../../../store/api/manager-api';
import { resetCachedData } from '../../../store/api/utils.ts/cache';
import { useAlertStore } from '../../../store/use-alert-store';
import { useAuthStore } from '../../../store/use-auth-store';
import { PermissionServiceType } from '../../../store/use-manager-store';
import { IdValue } from '../../../types/props';
import { navigationTabs } from './navigation-tabs';

export type MenuTabType = {
  icon?: ReactNode;
  subMenu?: MenuTabType[];
  href?: string;
  defaultParams?: string;
  service?: ServiceType;
  userRole?: UserRole;
  permissionsService?: PermissionServiceType;
} & IdValue;

export const useNavigation = () => {
  const navigate = useNavigate();
  const { addQueryParams, getQueryParams } = useQueryParams();
  const { logout } = useAuthStore();
  const { reset } = useAlertStore();

  const params = getQueryParams();
  const isMobile = useIsMobile();
  const isMobileOpen = isMobile && params.nav === '1';

  const [selected, setSelected] = useState<string>();

  const handleSelected = (id: string) => {
    setSelected(selected === id ? undefined : id);
  };

  const { pathname, search } = useLocation();

  const paths = pathname.split('/');

  const path1 = paths[2];
  const path2 = paths[2] + '/' + paths[3];

  const { t } = useLang();

  const handleLogout = async () => {
    localStorage.removeItem(accessTokenKey);
    localStorage.removeItem(refreshTokenKey);

    if ('serviceWorker' in navigator) {
      const isExist = await navigator.serviceWorker.getRegistrations();
      if (isExist) {
        isExist.forEach(async (sw) => sw.unregister());
      }
    }
    await logout();
    reset();
    resetCachedData();
    navigate(concatPath([RootPath.Manager, ManagerPath.Auth, AuthPath.Login]));
    window.location.reload();
  };

  useEffect(() => {
    if (isMobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileOpen]);

  return {
    path1,
    path2,
    t,
    navigationTabs,
    navigate,
    selected,
    handleSelected,
    search,
    addQueryParams,
    isMobileOpen,
    handleLogout,
  };
};
